// Day handling for the schedule, the map and the route sheets.
//
// A job's date reaches the browser in one of two spellings: MM/DD/YYYY, which
// the scheduler writes, or YYYY-MM-DD, which a calendar or CSV import writes.
// Neither can be handed to `new Date()` and formatted back out through UTC.
// `new Date('2026-09-30')` is midnight UTC, which is the evening of the 29th in
// California, so a job booked for the 30th lands on the 29th and the week grid
// starts on the wrong weekday. `new Date()` formatted through `toISOString()`
// has the same fault in reverse: after 5pm Pacific it reports tomorrow, so the
// schedule opens on a day the office has not reached yet.
//
// Everything here therefore works on the local calendar date, never on an
// instant in time.

const pad = (n) => String(n).padStart(2, '0');

const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})/;
const US = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;
const LOCAL_DATETIME = /^\d{4}-\d{1,2}-\d{1,2}[T ]\d{2}:\d{2}/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;
const YEAR_MONTH = /^(\d{4})-(\d{1,2})$/;

/**
 * A Date, or anything Date can read, as YYYY-MM-DD in the viewer's own zone.
 *
 * A value that is ALREADY a calendar date is returned as one without going
 * through `new Date()`: `new Date('2026-09-30')` is midnight UTC, which is the
 * evening of the 29th here, so routing a stored date through an instant moves
 * it a day. That makes this agree with `normDate` on strings instead of
 * disagreeing with it. A null or an empty string is empty, not the epoch —
 * `new Date(null)` is 1 Jan 1970, which rendered as "1969-12-31".
 */
export const ymd = (d = new Date()) => {
  if (d === null || d === '') return '';
  if (typeof d === 'string') {
    const str = d.trim();
    const iso = ISO.exec(str);
    // Only when the whole string is a date, or a wall-clock reading with no
    // zone. "2026-09-21T08:00" is a time someone wrote, not an instant.
    if (iso && (str.length === 10 || (LOCAL_DATETIME.test(str) && !HAS_ZONE.test(str)))) {
      return `${iso[1]}-${pad(iso[2])}-${pad(iso[3])}`;
    }
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt)) return '';
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

/** Today, as the office would write it. */
export const today = () => ymd(new Date());

// Both names are in use across the app and both are correct; this is an alias
// rather than a second implementation so they can never drift apart.
export const todayISO = today;

/** The current month as YYYY-MM. */
export const thisMonth = () => today().slice(0, 7);

/**
 * A stored date in either spelling, as YYYY-MM-DD. The two known spellings are
 * rewritten by hand rather than parsed, so no timezone is involved; anything
 * else falls back to Date and, failing that, to the first ten characters.
 */
export const normDate = (s) => {
  if (!s) return '';
  if (s instanceof Date) return ymd(s);
  const str = String(s).trim();
  const iso = ISO.exec(str);
  if (iso) return `${iso[1]}-${pad(iso[2])}-${pad(iso[3])}`;
  const us = US.exec(str);
  if (us) return `${us[3]}-${pad(us[1])}-${pad(us[2])}`;
  const dt = new Date(str);
  return isNaN(dt) ? str.slice(0, 10) : ymd(dt);
};

/** A date string as a Date at local midnight, safe to shift and to format. */
export const parseDay = (s) => {
  const m = ISO.exec(normDate(s));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const dt = new Date(s);
  return isNaN(dt) ? new Date(NaN) : dt;
};

/** `n` days from a date string, as a date string. Negative goes backwards. */
export const shiftDays = (s, n) => {
  const d = parseDay(s);
  if (isNaN(d)) return '';
  d.setDate(d.getDate() + n);
  return ymd(d);
};

/** The seven days of the week a date falls in, Sunday first. */
export const weekOf = (s) => {
  const base = parseDay(s);
  if (isNaN(base)) return [];
  const start = new Date(base);
  start.setDate(base.getDate() - base.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return ymd(d);
  });
};

export const prettyDate = (d) => parseDay(d).toLocaleDateString(undefined,
  { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

// "Fri, Sep 19, 2026" rather than "Friday, September 19, 2026". The long form is
// 231px of a phone's 309px of usable row, which leaves no room for the arrow
// that moves to the next day.
export const shortDate = (d) => parseDay(d).toLocaleDateString(undefined,
  { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

/** Whether a stored date is the day the viewer is having. */
export const isToday = (s) => !!s && normDate(s) === today();

/** `n` months from a date string, as a date string. Negative goes backwards. */
export const shiftMonths = (s, n) => {
  // A caller working in months passes YYYY-MM and expects YYYY-MM back. Without
  // this, "2026-10" falls through to `new Date("2026-10")` - UTC 1 October,
  // which is 30 September here - and a month step lands in the wrong month.
  const ym = YEAR_MONTH.exec(String(s || '').trim());
  if (ym) {
    const shifted = new Date(Number(ym[1]), Number(ym[2]) - 1 + n, 1);
    return ymd(shifted).slice(0, 7);
  }
  const d = parseDay(s);
  if (isNaN(d)) return '';
  // Clamp rather than overflow: a month shift from the 31st must not land in the
  // month after the one being asked for. 31 Jan back one month is 28 Feb here,
  // not 3 March, which is what setMonth would give on its own.
  const day = d.getDate();
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastOfTarget = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return ymd(new Date(first.getFullYear(), first.getMonth(), Math.min(day, lastOfTarget)));
};

/**
 * The calendar grid for the month a date falls in: whole weeks, Sunday first,
 * so the leading and trailing days belong to the neighbouring months. Always six
 * rows, so the grid does not change height from one month to the next.
 */
export const monthGrid = (s) => {
  const base = parseDay(s);
  if (isNaN(base)) return [];
  const first = new Date(base.getFullYear(), base.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { date: ymd(d), inMonth: d.getMonth() === base.getMonth() };
  });
};

/** "October 2026", for a month heading. */
export const monthLabel = (d) => parseDay(d).toLocaleDateString(undefined,
  { month: 'long', year: 'numeric' });

// --- carried over from the other line of work ------------------------------
//
// These four have no equivalent above. `daysBetween` and `formatDate` exist
// because the same two UTC mistakes bite when measuring a gap and when writing
// a date out; `parseHour` reads a time as a person writes it on a schedule.

/** Whole days from `from` to `to`, by calendar date rather than elapsed time. */
export const daysBetween = (from, to) => {
  const a = normDate(from), b = normDate(to);
  if (!a || !b) return 0;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  // Through UTC deliberately: both ends are already calendar dates, and only
  // the difference is wanted, so a clock change must not add or drop an hour.
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
};

/**
 * The hour of day (0–23) from a time as people write it on a schedule:
 * "8:00 AM", "8 AM", "14:30", "2:15 PM". Null when there is nothing to read,
 * which callers sort to the end rather than treating as midnight.
 */
export const parseHour = (value) => {
  if (!value) return null;
  const m = String(value).match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  if (isNaN(h)) return null;
  const meridiem = (m[3] || '').toUpperCase();
  if (meridiem === 'PM' && h < 12) h += 12;
  if (meridiem === 'AM' && h === 12) h = 0;
  return h >= 0 && h <= 23 ? h : null;
};

/**
 * A calendar date written out for a person, with the format left to the caller.
 *
 * `prettyDate` and `shortDate` above are the two standard shapes; this is for
 * anything else. `new Date("2026-09-21").toLocaleDateString()` renders "Sep 20"
 * in California — the string parses as UTC midnight, which is 5pm the previous
 * day here — so the schedule's date bar sat a day behind the day it filtered.
 *
 * Use this for a date. A true timestamp (createdAt, lastLoginAt) is an instant,
 * and `new Date(...)` is right for those.
 */
export const formatDate = (
  value,
  options = { year: 'numeric', month: 'short', day: 'numeric' },
  locale = undefined,
) => {
  const iso = normDate(value);
  if (!iso) return '';
  const d = parseDay(iso);
  return isNaN(d) ? '' : d.toLocaleDateString(locale, options);
};
